import React, { useState, useEffect } from 'react';
import { notification } from '../../hocs/notification';
import axios from 'axios';
import { mainurl } from '../../../Mainurl';

import { 
    Card, CardHeader, CardBody, Button, Row, Col, Form, FormGroup, 
    Label, Input, Alert, Spinner, Badge, Progress 
} from 'reactstrap';
import { 
    FaFileAlt, FaArrowLeft, FaUpload, FaCheckCircle, 
    FaClock, FaExclamationTriangle, FaSave, FaList,
    FaCalendarAlt, FaInfoCircle
} from 'react-icons/fa';

const AddCompliance = ({ back, ...props }) => {
    const [complianceType, setComplianceType] = useState('');
    const [complianceTypeOpt, setComplianceTypeOpt] = useState('');
    const [complianceNo, setComplianceNo] = useState('');
    const [complianceDesc, setComplianceDesc] = useState('');
    const [frequency, setFrequency] = useState('');
    const [complianceStatus, setComplianceStatus] = useState('');
    const [complianceFile, setComplianceFile] = useState('');

    // Enhanced state for better UX
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [selectedFileName, setSelectedFileName] = useState('');

    useEffect(() => {
        get_compliance_type_list();
    }, []);

    // Validation function
    const validateForm = () => {
        const errors = {};
        if (!complianceType || complianceType === '0') errors.complianceType = 'Please select a compliance type';
        if (!complianceNo.trim()) errors.complianceNo = 'Please enter compliance number';
        if (!complianceDesc.trim()) errors.complianceDesc = 'Please enter compliance description';
        if (!frequency || frequency === '0') errors.frequency = 'Please select frequency';
        if (!complianceStatus || complianceStatus === '0') errors.complianceStatus = 'Please select status';
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const get_compliance_type_list = () => {
        setLoading(true);
        axios({
            method: 'post',
            url: mainurl + 'Master/compliance_type_list',
            headers: { 'Content-Type': 'application/JSON', }
        })
            .then(function (response) {
                let list = response.data.map((row, index) => {
                    return(
                        <option key={index} value={row.compliance_type_id}> {row.compliance_name} </option>
                    )
                });

                setComplianceTypeOpt(list);
                setLoading(false);
            })
            .catch(error => {
                setLoading(false);
                let notify = notification({ message: "Failed to load compliance types! Try Again", type: 'error' });
                notify();
            });
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFileName(file.name);
            setComplianceFile(e.target.value);
        } else {
            setSelectedFileName('');
            setComplianceFile('');
        }
    };

    const add_compliance_handler = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitLoading(true);

        let postData = [{
            "org_id": sessionStorage.getItem('org_id'),
            "compliance_type_id": complianceType,
            "compliance_no": complianceNo,
            "compliance_desc": complianceDesc,
            "frequency": frequency,
            "status": complianceStatus
        }]

        axios({
            method: 'post',
            url: mainurl + 'Master/add_compliance',
            data: postData,
            headers: { 'Content-Type': 'application/JSON', }
        })
        .then(function (response) {
            let res = response.data[0].retcode;
            if(res === "101"){
                setSuccessMessage("Compliance added successfully!");
                setTimeout(() => {
                    let notify = notification({ message: "Compliance added Successfully.", type: 'info', time:true, position:'center' });
                    notify();
                    back();
                }, 1500);
            }
            setSubmitLoading(false);
        })
        .catch(error => {
            setSubmitLoading(false);
            let notify = notification({ message: "Submit failed! Try Again", type: 'error', time:true });
            notify();
        });
    }

    const getStatusIcon = () => {
        switch(complianceStatus) {
            case '1': return <FaClock className="text-warning" />;
            case '2': return <FaCheckCircle className="text-success" />;
            default: return <FaInfoCircle className="text-primary" />;
        }
    };

    const getFrequencyBadge = () => {
        const frequencyMap = {
            '1': { label: 'Yearly', color: 'primary' },
            '2': { label: 'Half Yearly', color: 'info' },
            '3': { label: 'Quarterly', color: 'warning' },
            '4': { label: 'Monthly', color: 'success' },
            '5': { label: 'Weekly', color: 'danger' }
        };
        
        const freq = frequencyMap[frequency];
        return freq ? <Badge color={freq.color}>{freq.label}</Badge> : null;
    };

    return (
        <>
            {/* Success Alert */}
            {successMessage && (
                <Alert color="success" className="d-flex align-items-center mb-3">
                    <FaCheckCircle className="me-2" />
                    {successMessage}
                </Alert>
            )}

            {/* Main Form Card */}
            <Card className="shadow-sm border-primary">
                <CardHeader className='bg-primary text-white d-flex justify-content-between align-items-center'>
                    <div className="d-flex align-items-center">
                        <FaFileAlt className="me-2" />
                        <h5 className="mb-0">Add Statutory Compliance</h5>
                    </div>
                    <Button 
                        color="light" 
                        size="sm" 
                        onClick={back}
                        className="d-flex align-items-center"
                    > 
                        <FaArrowLeft className="me-1" /> Back
                    </Button>
                </CardHeader>
                <CardBody className="p-4">
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                            <p className="mt-2 text-primary">Loading compliance types...</p>
                        </div>
                    ) : (
                        <Form onSubmit={add_compliance_handler}>
                            <Row>
                                {/* Compliance Type */}
                                <Col lg={6} className="mb-4">
                                    <FormGroup>
                                        <Label for="complianceType" className="fw-bold text-primary mb-2">
                                            <FaList className="me-2" />
                                            Compliance Type <span className="text-danger">*</span>
                                        </Label>
                                        <Input 
                                            id="complianceType" 
                                            bsSize="sm" 
                                            type="select" 
                                            value={complianceType} 
                                            onChange={(e) => {
                                                setComplianceType(e.target.value);
                                                if (formErrors.complianceType) {
                                                    setFormErrors(prev => ({ ...prev, complianceType: '' }));
                                                }
                                            }}
                                            invalid={!!formErrors.complianceType}
                                            className="border-primary"
                                        >
                                            <option value="0">-- Select Compliance Type --</option>
                                            {complianceTypeOpt}
                                        </Input>
                                        {formErrors.complianceType && (
                                            <small className="text-danger">{formErrors.complianceType}</small>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Compliance Number */}
                                <Col lg={6} className="mb-4">
                                    <FormGroup>
                                        <Label for="complianceNo" className="fw-bold text-primary mb-2">
                                            <FaFileAlt className="me-2" />
                                            Compliance Number <span className="text-danger">*</span>
                                        </Label>
                                        <Input 
                                            id="complianceNo" 
                                            type='text' 
                                            bsSize="sm" 
                                            value={complianceNo} 
                                            onChange={(e) => {
                                                setComplianceNo(e.target.value);
                                                if (formErrors.complianceNo) {
                                                    setFormErrors(prev => ({ ...prev, complianceNo: '' }));
                                                }
                                            }}
                                            placeholder="Enter compliance number"
                                            invalid={!!formErrors.complianceNo}
                                            className="border-primary"
                                        />
                                        {formErrors.complianceNo && (
                                            <small className="text-danger">{formErrors.complianceNo}</small>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Compliance Description */}
                                <Col lg={12} className="mb-4">
                                    <FormGroup>
                                        <Label for="complianceDesc" className="fw-bold text-primary mb-2">
                                            <FaInfoCircle className="me-2" />
                                            Compliance Description <span className="text-danger">*</span>
                                        </Label>
                                        <Input 
                                            id="complianceDesc" 
                                            type='textarea' 
                                            bsSize="sm" 
                                            rows="3"
                                            value={complianceDesc} 
                                            onChange={(e) => {
                                                setComplianceDesc(e.target.value);
                                                if (formErrors.complianceDesc) {
                                                    setFormErrors(prev => ({ ...prev, complianceDesc: '' }));
                                                }
                                            }}
                                            placeholder="Enter detailed compliance description"
                                            invalid={!!formErrors.complianceDesc}
                                            className="border-primary"
                                        />
                                        {formErrors.complianceDesc && (
                                            <small className="text-danger">{formErrors.complianceDesc}</small>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Frequency */}
                                <Col lg={6} className="mb-4">
                                    <FormGroup>
                                        <Label for="frequency" className="fw-bold text-primary mb-2">
                                            <FaCalendarAlt className="me-2" />
                                            Frequency <span className="text-danger">*</span>
                                        </Label>
                                        <div className="d-flex align-items-center">
                                            <Input 
                                                id="frequency" 
                                                bsSize="sm" 
                                                type="select" 
                                                value={frequency} 
                                                onChange={(e) => {
                                                    setFrequency(e.target.value);
                                                    if (formErrors.frequency) {
                                                        setFormErrors(prev => ({ ...prev, frequency: '' }));
                                                    }
                                                }}
                                                invalid={!!formErrors.frequency}
                                                className="border-primary me-2"
                                            >
                                                <option value="0">-- Select Frequency --</option>
                                                <option value="1">Yearly</option>
                                                <option value="2">Half Yearly</option>
                                                <option value="3">Quarterly (Three monthly)</option>
                                                <option value="4">Monthly</option>
                                                <option value="5">Weekly</option>
                                            </Input>
                                            {getFrequencyBadge()}
                                        </div>
                                        {formErrors.frequency && (
                                            <small className="text-danger">{formErrors.frequency}</small>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Status */}
                                <Col lg={6} className="mb-4">
                                    <FormGroup>
                                        <Label for="status" className="fw-bold text-primary mb-2">
                                            {getStatusIcon()}
                                            <span className="ms-2">Status <span className="text-danger">*</span></span>
                                        </Label>
                                        <Input 
                                            id="status" 
                                            bsSize="sm" 
                                            type="select" 
                                            value={complianceStatus} 
                                            onChange={(e) => {
                                                setComplianceStatus(e.target.value);
                                                if (formErrors.complianceStatus) {
                                                    setFormErrors(prev => ({ ...prev, complianceStatus: '' }));
                                                }
                                            }}
                                            invalid={!!formErrors.complianceStatus}
                                            className="border-primary"
                                        >
                                            <option value="0">-- Select Status --</option>
                                            <option value="1">Pending</option>
                                            <option value="2">Completed</option>
                                        </Input>
                                        {formErrors.complianceStatus && (
                                            <small className="text-danger">{formErrors.complianceStatus}</small>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* File Upload */}
                                <Col lg={12} className="mb-4">
                                    <FormGroup>
                                        <Label for="complianceFile" className="fw-bold text-primary mb-2">
                                            <FaUpload className="me-2" />
                                            Attach Document (Optional)
                                        </Label>
                                        <div className="border border-primary rounded p-3 bg-primary bg-opacity-10">
                                            <Input 
                                                id="complianceFile" 
                                                bsSize="sm" 
                                                type="file" 
                                                onChange={handleFileChange}
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                className="border-0"
                                            />
                                            {selectedFileName && (
                                                <div className="mt-2 text-success">
                                                    <FaCheckCircle className="me-1" />
                                                    Selected: {selectedFileName}
                                                </div>
                                            )}
                                            <small className="text-muted">
                                                Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG
                                            </small>
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Form Summary */}
                            {(complianceType !== '0' || frequency !== '0' || complianceStatus !== '0') && (
                                <Card className="bg-primary bg-opacity-10 border-primary mb-4">
                                    <CardBody className="py-3">
                                        <h6 className="text-primary mb-2">
                                            <FaInfoCircle className="me-2" />
                                            Summary
                                        </h6>
                                        <Row>
                                            {frequency !== '0' && (
                                                <Col md={4} className="mb-2">
                                                    <strong>Frequency:</strong> {getFrequencyBadge()}
                                                </Col>
                                            )}
                                            {complianceStatus !== '0' && (
                                                <Col md={4} className="mb-2">
                                                    <strong>Status:</strong> 
                                                    <span className="ms-2">
                                                        {getStatusIcon()}
                                                        <span className="ms-1">
                                                            {complianceStatus === '1' ? 'Pending' : 'Completed'}
                                                        </span>
                                                    </span>
                                                </Col>
                                            )}
                                            {selectedFileName && (
                                                <Col md={4} className="mb-2">
                                                    <strong>Document:</strong> 
                                                    <span className="ms-2 text-success">
                                                        <FaCheckCircle className="me-1" />
                                                        Attached
                                                    </span>
                                                </Col>
                                            )}
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Submit Section */}
                            <hr className="border-primary" />
                            <Row>
                                <Col md={12} className="text-end">
                                    <Button 
                                        color='primary' 
                                        size="sm" 
                                        disabled={submitLoading}
                                        className="px-4 py-2"
                                        type="submit"
                                    > 
                                        {submitLoading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="me-2" />
                                                Submit Compliance
                                            </>
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    )}
                </CardBody>
            </Card>        
        </>
    )
}

export default AddCompliance;